import { useEffect, useState } from 'react'
import { Loader2, Wand2, X } from 'lucide-react'
import { api, apiErrorMessage } from '../lib/api'
import type { DraftOut, FacetsOut, RankedRowOut } from '../lib/api'

export function OutreachDrawer({
  target,
  useCase,
  facets,
  onClose,
}: {
  target: RankedRowOut | null
  useCase: string
  facets: FacetsOut | null
  onClose: () => void
}) {
  const [draft, setDraft] = useState<DraftOut | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (!target) {
      setDraft(null)
      setError(null)
      return
    }
    let cancel = false
    async function run() {
      setLoading(true)
      setError(null)
      setDraft(null)
      setCopied(false)
      try {
        const res = await api.post<DraftOut>('/outreach/draft', {
          profile_id: target!.profile_id,
          use_case: useCase,
          facets: facets ?? undefined,
        })
        if (!cancel) setDraft(res.data)
      } catch (err) {
        if (!cancel) setError(apiErrorMessage(err))
      } finally {
        if (!cancel) setLoading(false)
      }
    }
    run()
    return () => {
      cancel = true
    }
  }, [target, useCase, facets])

  if (!target) return null

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-[480px] max-w-full bg-bg border-l border-border h-full flex flex-col">
        <header className="flex items-start justify-between p-5 border-b border-border">
          <div>
            <div className="text-xs text-gray-500 flex items-center gap-1">
              <Wand2 size={12} /> Draft outreach
            </div>
            <div className="font-medium text-gray-100 mt-0.5">{target.full_name}</div>
            <div className="text-xs text-gray-400">
              {target.current_title}
              {target.current_company ? ` · ${target.current_company}` : ''}
            </div>
          </div>
          <button onClick={onClose} className="btn-ghost h-7 w-7 p-0">
            <X size={16} />
          </button>
        </header>

        <div className="flex-1 overflow-auto p-5 space-y-4">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <Loader2 size={16} className="animate-spin" />
              Drafting via Sonnet…
            </div>
          )}
          {error && (
            <div className="text-sm text-red-400 bg-red-950/40 border border-red-900/40 rounded-md px-3 py-2">
              {error}
            </div>
          )}
          {draft && (
            <>
              <textarea
                className="input min-h-[260px] text-[14px] leading-relaxed"
                value={draft.message}
                onChange={(e) => setDraft({ ...draft, message: e.target.value })}
              />
              <div className="flex items-center gap-2">
                <button
                  className="btn-secondary flex-1"
                  onClick={async () => {
                    await navigator.clipboard.writeText(draft.message)
                    setCopied(true)
                    setTimeout(() => setCopied(false), 1500)
                  }}
                >
                  {copied ? 'Copied ✓' : 'Copy to clipboard'}
                </button>
                <a
                  href={draft.target_url}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-primary flex-1"
                >
                  Open LinkedIn
                </a>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
